import { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { LoadingState, EmptyState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { queueStatusUrl } from '@/lib/publicLinks';
import { usePatients } from '../patients/usePatients';
import type { Patient } from '../patients/types';
import { useDoctors } from './useDoctors';
import { useAppointmentSlots } from './useAppointmentSlots';
import { useBookSlot } from './useBookSlot';
import { useCancelSlot } from './useCancelSlot';
import type { AppointmentSlot } from './types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/lib/queryKeys';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Badge } from '@/components/ui/Badge';
import { markNoShow } from './api';
import { AppointmentsCalendar } from './AppointmentsCalendar';

type BookView = 'day' | 'week' | 'month';

/**
 * Third screen migrated off Create React App — see HMS_V4_API_CONTRACT.md
 * and QueueView.tsx for the established pattern: a drop-in replacement for
 * src/components/AppointmentsModule.jsx with the identical single prop,
 * swapped into App.jsx by changing one import line.
 *
 * Reception-facing booking for phone/walk-in requests that don't go through
 * the self-service kiosk — pick a doctor + date, see that day's open slots,
 * search for a patient, book. Deliberately a visual reproduction, not a
 * redesign — see Button.tsx's reasoning.
 */

interface AppointmentsModuleProps {
  onStartVisit?: (patient: Patient) => void;
}

function fmtTime(t: string | null | undefined): string {
  return (t || '').slice(0, 5);
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function AppointmentsModule({ onStartVisit }: AppointmentsModuleProps) {
  const [doctorId, setDoctorId] = useState('');
  const [date, setDate] = useState(todayStr());
  const [bookingSlot, setBookingSlot] = useState<AppointmentSlot | null>(null);
  const [patientQuery, setPatientQuery] = useState('');
  const [confirmed, setConfirmed] = useState<AppointmentSlot | null>(null);
  // G6: week/month overview, recorded no-shows and rebooking.
  const [view, setView] = useState<BookView>('day');
  const [rebook, setRebook] = useState<{ slotId: number; patientId: number; name: string } | null>(null);
  const queryClient = useQueryClient();
  const noShowMutation = useMutation({
    mutationFn: (slotId: number) => markNoShow(slotId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.appointments.slots() }),
  });

  const doctorsQuery = useDoctors();
  const patientsQuery = usePatients();
  const slotsQuery = useAppointmentSlots({ doctor_id: doctorId, date, status: 'all' });
  const bookMutation = useBookSlot();
  const cancelMutation = useCancelSlot();

  // Default to the first doctor once the roster loads, same as the legacy
  // .then(rows => rows.length && setDoctorId(rows[0].id)) — but only if
  // nothing has been picked yet, so a later refetch never yanks a doctor
  // the receptionist already selected out from under them.
  useEffect(() => {
    if (!doctorId && doctorsQuery.data?.length) {
      setDoctorId(String(doctorsQuery.data[0]!.id));
    }
  }, [doctorId, doctorsQuery.data]);

  const doctors = doctorsQuery.data ?? [];
  const patients = patientsQuery.data ?? [];
  const slots = slotsQuery.data ?? null;

  const patientMatches = useMemo(() => {
    const q = patientQuery.trim().toLowerCase();
    if (!q) return [];
    return patients
      .filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          p.uhid?.toLowerCase().includes(q) ||
          p.mobile?.includes(patientQuery)
      )
      .slice(0, 15);
  }, [patients, patientQuery]);

  // A slot generated for earlier today that nobody booked is still 'open' in
  // the DB (nothing ever flips it once its time passes) — filtered out here
  // rather than showing reception a slot that's already gone.
  const now = new Date();
  const isPast = (s: AppointmentSlot) => date === todayStr() && new Date(`${date}T${s.start_time}`) < now;
  const openSlots = (slots || []).filter((s) => s.status === 'open' && !isPast(s));
  const bookedSlots = (slots || []).filter((s) => s.status === 'booked');
  const noShowSlots = (slots || []).filter((s) => s.status === 'no_show');
  const started = (s: AppointmentSlot) => new Date(`${date}T${s.start_time}`) <= now;

  const startBooking = (slot: AppointmentSlot) => {
    if (rebook) {
      // Rebooking a no-show: the patient is already known.
      bookMutation.mutate(
        { slotId: slot.id, patientId: rebook.patientId, rebookOfSlotId: rebook.slotId },
        { onSuccess: (booked) => { setConfirmed(booked); setRebook(null); } },
      );
      return;
    }
    setBookingSlot(slot);
    setPatientQuery('');
    setConfirmed(null);
    bookMutation.reset();
  };

  const book = (patientId: number) => {
    if (!bookingSlot) return;
    bookMutation.mutate(
      { slotId: bookingSlot.id, patientId },
      {
        onSuccess: (booked) => {
          setConfirmed(booked);
          setBookingSlot(null);
          setPatientQuery('');
        },
      }
    );
  };

  const cancel = (slotId: number) => {
    cancelMutation.mutate({ slotId, reason: 'Cancelled by reception' });
  };

  const bookError = bookMutation.error instanceof ApiError ? bookMutation.error.message : null;
  const cancelError = cancelMutation.error instanceof ApiError ? cancelMutation.error.message
    : noShowMutation.error instanceof ApiError ? noShowMutation.error.message : null;

  return (
    <div className="fade-in">
      <div className="mb-4 text-[13px] text-ink-muted">
        Book a patient into an open slot — for phone bookings or walk-in requests. Patients booking themselves use
        the self-service kiosk.
      </div>

      {(bookError || cancelError) && (
        <div className="mb-3.5 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">
          {bookError || cancelError}
        </div>
      )}

      {confirmed && (
        <div className="mb-3.5 rounded-[var(--radius)] border border-success-soft bg-success-soft px-3.5 py-2.5 text-[13px] text-success-ink">
          <div className="mb-1.5">
            ✓ Booked {fmtTime(confirmed.start_time)} on {date} — confirmation sent to the patient.
          </div>
          {confirmed.status_token && (
            <a href={queueStatusUrl(confirmed.status_token)} target="_blank" rel="noopener noreferrer" className="font-semibold text-teal">
              🔗 Open patient status page →
            </a>
          )}
        </div>
      )}

      <div className="mb-3 flex justify-end">
        <SegmentedControl<BookView> size="sm" value={view} onChange={setView}
          options={[{ value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }, { value: 'month', label: 'Month' }]} />
      </div>

      <Card className="mb-5 flex flex-wrap gap-4 p-3.5">
        <FormField label="Doctor">
          <NativeSelect value={doctorId} onChange={(e) => setDoctorId(e.target.value)} className="min-w-[220px]">
            {doctors.map((d) => (
              <option key={d.id} value={d.id}>
                {d.full_name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField label="Date">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </FormField>
      </Card>

      {view !== 'day' ? (
        <AppointmentsCalendar mode={view} anchor={date} doctorId={doctorId} today={todayStr()}
          onAnchorChange={setDate} onOpenDay={(d) => { setDate(d); setView('day'); }} />
      ) : (<>
      {rebook && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-[var(--radius)] border border-info/40 bg-info-soft px-3 py-2 text-[12.5px] text-info-ink">
          Rebooking <b>{rebook.name}</b> — choose the doctor, date and an open slot below.
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setRebook(null)}>Stop rebooking</Button>
        </div>
      )}
      <div className="mb-2 text-[13.5px] font-bold">Open slots</div>
      {slotsQuery.isLoading ? (
        <LoadingState className="mb-6" />
      ) : slotsQuery.isError ? (
        <ErrorState
          className="mb-6"
          message={slotsQuery.error instanceof ApiError ? slotsQuery.error.message : 'Failed to load slots'}
          onRetry={() => slotsQuery.refetch()}
        />
      ) : openSlots.length === 0 ? (
        <EmptyState className="mb-6" label="No open slots for this doctor on this date." />
      ) : (
        <div className="mb-6 flex flex-wrap gap-2">
          {openSlots.map((s) => (
            <Button key={s.id} variant="ghost" size="sm" onClick={() => startBooking(s)}>
              {fmtTime(s.start_time)}–{fmtTime(s.end_time)}
            </Button>
          ))}
        </div>
      )}

      {bookingSlot && (
        <Card className="mb-5">
          <div className="mb-2.5 flex items-center justify-between">
            <div className="text-[13.5px] font-bold">
              Book {fmtTime(bookingSlot.start_time)}–{fmtTime(bookingSlot.end_time)}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setBookingSlot(null)}>
              Cancel
            </Button>
          </div>
          <Input
            placeholder="Search patient by name, UHID, mobile…"
            value={patientQuery}
            onChange={(e) => setPatientQuery(e.target.value)}
            autoFocus
          />
          {patientQuery.trim() && (
            <div className="mt-1.5 max-h-[220px] overflow-y-auto rounded-[var(--radius)] border border-line">
              {patientMatches.length === 0 ? (
                <div className="p-3 text-center text-xs text-ink-muted">No patients found for &quot;{patientQuery}&quot;</div>
              ) : (
                patientMatches.map((p) => (
                  <div
                    key={p.id}
                    onClick={() => book(p.id)}
                    className="flex cursor-pointer items-center justify-between border-b border-[#F3F4F6] px-3 py-2.5 last:border-b-0 hover:bg-canvas"
                  >
                    <div>
                      <div className="text-[13px] font-semibold">{p.name}</div>
                      <div className="text-[11px] text-ink-muted">
                        {p.uhid} · {p.gender}, {p.age}y{p.mobile ? ` · ${p.mobile}` : ''}
                      </div>
                    </div>
                    <span className="text-base text-teal">→</span>
                  </div>
                ))
              )}
            </div>
          )}
        </Card>
      )}

      <div className="mb-2 text-[13.5px] font-bold">Booked appointments</div>
      {slots !== null && bookedSlots.length === 0 && <EmptyState label="No bookings yet for this doctor on this date." />}
      {bookedSlots.map((s) => {
        // Only meaningful for today's bookings — starting a visit for a
        // future-dated appointment doesn't make sense before that day
        // arrives. Needs the full patient record (startReturningVisit's
        // shape in VisitWorkflow.jsx), not just the name/mobile this slot
        // itself carries.
        const patient = patients.find((p) => p.id === s.patient_id);
        return (
          <Card key={s.id} className="mb-2 flex items-center justify-between px-3.5 py-2.5">
            <div>
              <span className="text-[13px] font-semibold">
                {fmtTime(s.start_time)}–{fmtTime(s.end_time)}
              </span>
              <span className="ml-2.5 text-xs text-ink-muted">
                {s.patient_name} ({s.patient_mobile})
              </span>
            </div>
            <div className="flex items-center gap-2.5">
              {date === todayStr() && patient && onStartVisit && (
                <Button size="sm" onClick={() => onStartVisit(patient)}>
                  🩺 Start intake →
                </Button>
              )}
              {s.status_token && (
                <a href={queueStatusUrl(s.status_token)} target="_blank" rel="noopener noreferrer" className="text-[11px] font-semibold text-teal">
                  🔗 Status page
                </a>
              )}
              <Button
                variant="ghost"
                size="sm"
                loading={cancelMutation.isPending && cancelMutation.variables?.slotId === s.id}
                onClick={() => cancel(s.id)}
              >
                Cancel &amp; notify
              </Button>
              {started(s) && (
                <Button variant="ghost" size="sm"
                  loading={noShowMutation.isPending && noShowMutation.variables === s.id}
                  onClick={() => noShowMutation.mutate(s.id)}>
                  Mark no-show
                </Button>
              )}
            </div>
          </Card>
        );
      })}

      {noShowSlots.length > 0 && (
        <>
          <div className="mb-2 mt-5 text-[13.5px] font-bold">No-shows</div>
          {noShowSlots.map((s) => (
            <Card key={s.id} className="mb-2 flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] font-semibold">{fmtTime(s.start_time)}–{fmtTime(s.end_time)}</span>
                <span className="text-xs text-ink-muted">{s.patient_name} ({s.patient_mobile})</span>
                <Badge variant="danger">No-show</Badge>
                <span className="text-[11px] text-ink-muted">
                  {s.no_show_source === 'staff' ? `marked by ${s.no_show_by ?? 'staff'}` : 'recorded at the end of the day'}
                </span>
              </div>
              {s.rescheduled_to_slot_id
                ? <Badge variant="success">Rebooked</Badge>
                : s.patient_id && (
                  <Button size="sm" variant="ghost" onClick={() => setRebook({ slotId: s.id, patientId: s.patient_id!, name: s.patient_name ?? 'patient' })}>
                    Rebook…
                  </Button>
                )}
            </Card>
          ))}
        </>
      )}
      </>)}
    </div>
  );
}
